// useSentrySpan.js
import { useMemo, useEffect, useCallback } from 'react';
import { startInactiveSpan } from '@sentry/browser';
import { SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN } from '@sentry/core';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';

import { getViewId } from '../resource/mixpanel.js';
import usePreviousValue from '../hook/usePreviousValue.js';
import useUnmount from '../hook/useUnmount.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  FEATURE_SENTRY_SPAN_PROFILER,
  SENTRY_SPAN_CATEGORY_THRESHOLD,
  SENTRY_SPAN_TIMEOUT_MSEC,
} from '../RemoteConfigKeys.js';
import { getHomePageConfig } from '../resource/homeUtils.js';
import { getIsSafari } from '../resource/getUserAgent.js';

const spanMap = new Map();

export const useSentryStartSpan = ({ shouldEnable = true } = {}) => {
  const { pathname } = useLocation();
  const isEnabled =
    useSelector(state =>
      getRemoteConfigData(state, FEATURE_SENTRY_SPAN_PROFILER)
    ) && shouldEnable;
  const spanTimeoutMsec = useSelector(state =>
    getRemoteConfigData(state, SENTRY_SPAN_TIMEOUT_MSEC)
  );

  const viewId = useMemo(() => getViewId({ pathname }), [pathname]);
  const prevViewId = usePreviousValue(viewId);

  const span = useMemo(() => {
    if (!isEnabled) return null;

    if (prevViewId && prevViewId !== viewId && spanMap.has(prevViewId)) {
      const prevSpan = spanMap.get(prevViewId);
      // in sentry config, beforeSendTransaction, will not send the event if the span has cancelled attribute
      prevSpan.setAttribute('cancelled', true);
      prevSpan.end();
      spanMap.delete(prevViewId);
    }

    if (!spanMap.has(viewId)) {
      const span = startInactiveSpan({
        name: `<${viewId}>`,
        op: 'custom.react.profiler',
        attributes: {
          [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'custom.react.profiler',
          'ui.view_id': viewId,
        },
      });

      spanMap.set(viewId, span);
    }

    return spanMap.get(viewId);
  }, [isEnabled, viewId, prevViewId]);

  useEffect(() => {
    if (!span) return;

    const timer = setTimeout(() => {
      // after X seconds, the span does not execute span.end
      if (spanMap.has(viewId)) {
        const span = spanMap.get(viewId);
        span.end();
        spanMap.delete(viewId);

        sendEventToSentry({ viewId, span });
      }
    }, spanTimeoutMsec);

    return () => clearTimeout(timer);
  }, [span, viewId, spanTimeoutMsec]);

  useUnmount(() => {
    if (spanMap.has(viewId)) {
      const span = spanMap.get(viewId);
      span.setAttribute('cancelled', true);
      span.end();
      spanMap.delete(viewId);
    }
  });

  return { span };
};

export const useSentryEndSpan = () => {
  const { pathname } = useLocation();
  const { categoriesRemoteConfigKey } = useMemo(
    () => getHomePageConfig({ pathname }),
    [pathname]
  );
  const remoteConfigCategories = useSelector(state =>
    getRemoteConfigData(state, categoriesRemoteConfigKey)
  );
  const sentrySpanCategoryThreshold = useSelector(state =>
    getRemoteConfigData(state, SENTRY_SPAN_CATEGORY_THRESHOLD)
  );

  const viewId = useMemo(() => getViewId({ pathname }), [pathname]);

  /**
   * Ends the Sentry span for the current view if conditions are met
   * @param {Object} options - Options for ending the span
   * @param {string} [options.category] - Category to check against remote config threshold
   * @returns {void}
   */
  const endSpan = useCallback(
    ({ category } = {}) => {
      if (spanMap.has(viewId)) {
        const span = spanMap.get(viewId);

        if (category) {
          const categoryIndex = remoteConfigCategories.findIndex(
            remoteCategory => remoteCategory === category
          );

          if (
            categoryIndex <= sentrySpanCategoryThreshold &&
            categoryIndex !== -1
          ) {
            span.setAttribute('category', category);
            span.end();
            spanMap.delete(viewId);
          }
        } else {
          span.end();
          spanMap.delete(viewId);
        }
      }
    },
    [viewId, remoteConfigCategories, sentrySpanCategoryThreshold]
  );

  return { endSpan };
};

const sendEventToSentry = async ({ span, viewId }) => {
  const [{ withScope }, { default: captureScreen }] = await Promise.all([
    import('@sentry/browser'),
    import('../resource/captureScreen.js'),
  ]);

  withScope(async scope => {
    scope.setTag('record', 'web-halted');

    if (span) {
      const spanData = span.getSpanJSON();
      scope.setExtra('span_data', spanData);
      scope.setTag('span_op', spanData.op);
      scope.setTag('span_description', spanData.description);

      if (spanData.data) {
        Object.keys(spanData.data).forEach(key => {
          scope.setExtra(`span_${key}`, spanData.data[key]);
        });
      }
    }

    try {
      const imageType = getIsSafari() ? 'image/jpeg' : 'image/webp';
      const bodyScreen = await captureScreen({
        element: document.body,
        options: {
          useCORS: true,
          backgroundColor: '#333',
          scale: 0.9,
          x: 0,
          y: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        },
        type: imageType,
      });

      scope.addAttachment({
        filename: `web_halted_${viewId}.jpeg`,
        data: bodyScreen,
      });
    } catch (error) {
      scope.setExtra('captureScreenError', error);
    }

    scope.captureException(new Error(`Web halted - ${viewId}`));
  });
};
